import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton
} from '@mui/material';
import DOMPurify from 'dompurify';
import closeIcon from '../Assets/images/close_red.png';

const TextDetailDialog = memo(({ open, onClose, title, text }) => {
    // Process and sanitize content
    const processedContent = useMemo(() => {
        if (!text) return '';

        // Step 1: Convert line breaks to HTML <br> tags
        // Handle both \r\n (Windows) and \n (Unix) line endings
        let processed = text.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>').replace(/\r/g, '<br>');

        // Step 2: Sanitize with permissive config for email HTML
        const sanitized = DOMPurify.sanitize(processed, {
            ALLOWED_TAGS: [
                // Text formatting
                'b', 'i', 'u', 'strong', 'em', 's', 'strike', 'del', 'mark', 'small', 'sub', 'sup',
                // Links
                'a',
                // Line breaks and paragraphs
                'br', 'p', 'div', 'span', 'pre', 'code',
                // Headings
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                // Lists
                'ul', 'ol', 'li',
                // Tables (for email formatting)
                'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
                // Blockquotes
                'blockquote', 'q', 'cite',
                // Other
                'hr', 'img'
            ],
            ALLOWED_ATTR: [
                'href', 'target', 'rel', 'style', 'class', 'id', 
                'colspan', 'rowspan', 'cellpadding', 'cellspacing', 'border',
                'src', 'alt', 'width', 'height', 'align', 'valign'
            ],
            ALLOW_DATA_ATTR: false,
            // Add target="_blank" to all links for security
            ADD_ATTR: ['target'],
        });

        // Step 3: Add target="_blank" and rel="noopener noreferrer" to links
        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitized, 'text/html');
        const links = doc.querySelectorAll('a');
        links.forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });

        return doc.body.innerHTML;
    }, [text]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            onClick={(e) => e.stopPropagation()} 
        >
            <DialogTitle sx={{ 
                m: 0, 
                p: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" component="span">
                    {title}
                </Typography>
                <IconButton onClick={onClose}>
                    <img src={closeIcon} alt="close" style={{ height: "35px" }} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div 
                    className="synthesized-detail-content"
                    dangerouslySetInnerHTML={{ __html: processedContent }}
                    style={{ 
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: 'rgba(0, 0, 0, 0.87)',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowY: 'auto',
                        maxHeight: '60vh'
                    }}
                />
            </DialogContent>
        </Dialog>
    );
});

TextDetailDialog.displayName = 'TextDetailDialog';

TextDetailDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    text: PropTypes.string
};

TextDetailDialog.defaultProps = {
    title: 'Note Details',
    text: ''
};

export default TextDetailDialog;

